import { useState } from 'react'
import { Stack, Title, Button, Group, TextInput, Select, Modal, Text } from '@mantine/core'
import { IconPlus, IconSearch, IconFilter } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { volumesApi } from '@/api'
import { VolumeTable } from '@/components/VolumeTable'
import { CreateVolumeModal } from '@/components/CreateVolumeModal'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'

export function Volumes() {
  const [search, setSearch] = useState('')
  const [driverFilter, setDriverFilter] = useState<string | null>(null)
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [deleteVolume, setDeleteVolume] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: volumesData, isLoading, refetch } = useQuery({
    queryKey: ['volumes', driverFilter],
    queryFn: async () => {
      const filters: any = {}
      if (driverFilter) filters.driver = driverFilter

      const { data } = await volumesApi.list(filters)
      return data
    },
    refetchInterval: 10000,
  })

  const deleteMutation = useMutation({
    mutationFn: (name: string) => volumesApi.delete(name),
    onSuccess: (_, name) => {
      notifications.show({
        title: 'Volume deleted',
        message: `Volume ${name} has been deleted successfully`,
        color: 'green',
      })
      setDeleteVolume(null)
      queryClient.invalidateQueries({ queryKey: ['volumes'] })
    },
    onError: (error: any, name) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || `Failed to delete volume ${name}`,
        color: 'red',
      })
      setDeleteVolume(null)
    },
  })

  const volumes = volumesData?.Volumes || []

  const filteredVolumes = volumes.filter((volume) =>
    volume.Name.toLowerCase().includes(search.toLowerCase())
  )

  const handleDelete = (name: string) => {
    setDeleteVolume(name)
  }

  const confirmDelete = () => {
    if (deleteVolume) {
      deleteMutation.mutate(deleteVolume)
    }
  }

  const driverOptions = [
    { value: '', label: 'All Drivers' },
    { value: 'local', label: 'local' },
    { value: 'nfs', label: 'nfs' },
    { value: 'cifs', label: 'cifs' },
    { value: 'glusterfs', label: 'glusterfs' },
    { value: 'ceph', label: 'ceph' },
  ]

  return (
    <>
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>Volumes</Title>
          <Button leftSection={<IconPlus size={18} />} onClick={openCreate}>
            Create Volume
          </Button>
        </Group>

        <Group>
          <TextInput
            placeholder="Search volumes..."
            leftSection={<IconSearch size={18} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by driver"
            leftSection={<IconFilter size={18} />}
            data={driverOptions}
            value={driverFilter}
            onChange={setDriverFilter}
            clearable
            style={{ width: 200 }}
          />
        </Group>

        <VolumeTable
          volumes={filteredVolumes}
          loading={isLoading}
          onDelete={handleDelete}
        />

        {volumesData?.Warnings && volumesData.Warnings.length > 0 && (
          <Stack gap="xs">
            {volumesData.Warnings.map((warning, index) => (
              <Text key={index} size="sm" c="yellow">
                Warning: {warning}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>

      <CreateVolumeModal
        opened={createOpened}
        onClose={closeCreate}
        onSuccess={() => {
          closeCreate()
          refetch()
        }}
      />

      <Modal
        opened={!!deleteVolume}
        onClose={() => setDeleteVolume(null)}
        title="Delete Volume"
        size="md"
      >
        <Stack>
          <Text>
            Are you sure you want to delete volume <strong>{deleteVolume}</strong>?
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone. The volume will be permanently removed from the system.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setDeleteVolume(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmDelete}
              loading={deleteMutation.isPending}
            >
              Delete Volume
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
